import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CheckCircle2, ClipboardList, Download, Filter, History, Repeat, Search, TimerOff, Users } from "lucide-react";
import { toast } from "sonner";
import { EmptyState, Spinner } from "./common";
import { TerritoryFilterSelects, useTerritoryMasters } from "./TerritoryFilter";
import { FollowUpRowActions, FollowUpStatusBadge, RepeatedBadge, FU_STATUS_META } from "./FollowUpActions";
import { staffLabelText } from "./StaffAvatar";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";
import { Switch } from "./ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { api, downloadBlob, errMsg, fmtDate, fmtDateTime, fmtINR, istDateISO } from "../lib/api";

/**
 * Phase 196 — Follow-up Visits report (Reports menu). One component for the three consoles:
 *   role="admin"      every follow-up (filters: scheduled-date range, Zone, Ward, Supervisor, Collector, status)
 *   role="supervisor" own + collectors under them (Zone / Ward locked to the territory server-side; Collector filter)
 *   role="collector"  own only (date range + status) — with Done / Reschedule actions on open rows
 * Columns: PIN, Owner, Ward, Zone, Mohalla, Collector / Supervisor, Last visit, Scheduled follow-up, Notes, Status
 * (Scheduled / Due today / Overdue / Done / Closed by newer visit), completion (date, reason, remarks), reschedules,
 * Repeated Follow-up badge (3+ follow-ups since the last payment). Excel export of the filtered rows.
 */
const todayISO = () => istDateISO();
const daysISO = (n) => istDateISO(new Date(Date.now() + n * 86400000));
const PAGE = 50;

function Kpi({ label, value, icon: Icon, tone = "", testId }) {
  return (
    <Card className={`shadow-sm ${tone}`}>
      <CardContent className="p-3 flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-background/70 border flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></div>
        <div className="min-w-0">
          <p className="text-lg font-semibold tabular-nums leading-tight" data-testid={testId}>{value}</p>
          <p className="text-[11px] text-muted-foreground truncate">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function FollowUpVisitsReport({ code, role = "admin", propertyPath, testId = "fu-report", canAct = false }) {
  const isAdmin = role === "admin";
  const isCollector = role === "collector";
  const [filters, setFilters] = useState({
    date_from: isCollector ? "" : daysISO(-30), date_to: isCollector ? "" : daysISO(30), status: isCollector ? "open" : "all",
    zone_id: "", ward_id: "", mohalla_id: "", supervisor_id: "", collector_id: "", repeated_only: false, search: "",
  });
  const masters = useTerritoryMasters(code);
  const [options, setOptions] = useState({ supervisors: [], collectors: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (isCollector) return;
    api.get(`/t/${code}/follow-up-visits/options`).then((r) => setOptions(r.data || { supervisors: [], collectors: [] })).catch(() => {});
  }, [code, isCollector]);

  const params = useMemo(() => {
    const p = { page, limit: PAGE };
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== false && v != null) p[k] = v; });
    return p;
  }, [filters, page]);

  const seq = React.useRef(0);
  const load = useCallback(() => {
    const s = ++seq.current;
    setLoading(true);
    api.get(`/t/${code}/follow-up-visits`, { params })
      .then((r) => { if (s === seq.current) setData(r.data); })
      .catch((e) => { if (s === seq.current) toast.error(errMsg(e)); })
      .finally(() => { if (s === seq.current) setLoading(false); });
  }, [code, params]);
  useEffect(() => { load(); }, [load]);
  const set = (patch) => { setPage(1); setFilters((f) => ({ ...f, ...patch })); };

  // Collector options follow the Supervisor filter (collectors whose wards sit in that supervisor's territory) and the
  // Zone / Ward filters; Supervisors follow the Zone filter.
  const zoneIds = (filters.zone_id || "").split(",").filter(Boolean);
  const wardIds = (filters.ward_id || "").split(",").filter(Boolean);
  const inArea = (u) => (!zoneIds.length || (u.zone_ids || []).some((z) => zoneIds.includes(z))) && (!wardIds.length || (u.ward_ids || []).some((w) => wardIds.includes(w)));
  const supervisors = (options.supervisors || []).filter(inArea);
  const collectors = (options.collectors || []).filter((c) => inArea(c) && (!filters.supervisor_id || (c.supervisor_ids || []).includes(filters.supervisor_id)));
  useEffect(() => {   // drop a selection that is no longer offered
    if (filters.supervisor_id && options.supervisors?.length && !supervisors.some((s) => s.id === filters.supervisor_id)) set({ supervisor_id: "", collector_id: "" });
    else if (filters.collector_id && options.collectors?.length && !collectors.some((c) => c.id === filters.collector_id)) set({ collector_id: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.zone_id, filters.ward_id, filters.supervisor_id, options]);

  const doExport = async () => {
    setExporting(true);
    try {
      const q = { ...params }; delete q.page; delete q.limit;
      await downloadBlob(`/t/${code}/follow-up-visits/export?${new URLSearchParams(q).toString()}`,
        `follow-up-visits_${filters.date_from || "all"}_to_${filters.date_to || "all"}.xlsx`);
      toast.success("Excel downloaded");
    } catch (e) { toast.error(errMsg(e)); } finally { setExporting(false); }
  };

  const s = data?.summary;
  const items = data?.items || [];
  const pages = Math.max(1, Math.ceil((data?.total || 0) / PAGE));
  const staffLabel = (r) => staffLabelText(r.staff_name, r.staff_employee_id);

  return (
    <div className="space-y-4" data-testid={testId}>
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid={`${testId}-kpis`}>
          <Kpi label="Follow-ups listed" value={s.total} icon={ClipboardList} testId={`${testId}-kpi-total`} />
          <Kpi label="Scheduled (upcoming)" value={s.scheduled || 0} icon={CalendarClock} tone="border-sky-200" testId={`${testId}-kpi-scheduled`} />
          <Kpi label="Due today" value={s.due_today || 0} icon={CalendarClock} tone="border-indigo-200" testId={`${testId}-kpi-due-today`} />
          <Kpi label="Overdue" value={s.overdue || 0} icon={TimerOff} tone={s.overdue ? "border-red-300 bg-red-50/40" : ""} testId={`${testId}-kpi-overdue`} />
          <Kpi label="Done" value={s.done || 0} icon={CheckCircle2} tone="border-emerald-200" testId={`${testId}-kpi-done`} />
          <Kpi label="Repeated follow-up properties" value={s.repeated_properties || 0} icon={Repeat} tone={s.repeated_properties ? "border-orange-300 bg-orange-50/40" : ""} testId={`${testId}-kpi-repeated`} />
        </div>
      )}

      <Card className="shadow-sm">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end" data-testid={`${testId}-filters`}>
          <div className="space-y-1">
            <Label className="text-xs">Scheduled from</Label>
            <Input type="date" className="w-36 h-10" value={filters.date_from} onChange={(e) => set({ date_from: e.target.value })} data-testid={`${testId}-filter-from`} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Scheduled to</Label>
            <Input type="date" className="w-36 h-10" value={filters.date_to} onChange={(e) => set({ date_to: e.target.value })} data-testid={`${testId}-filter-to`} />
          </div>
          {!isCollector && (
            <div className="basis-full md:basis-auto md:w-[380px]" data-testid={`${testId}-territory`}>
              <TerritoryFilterSelects masters={masters} levels={["zone", "ward"]} value={{ zoneId: filters.zone_id, wardId: filters.ward_id, mohallaId: "" }}
                onChange={(t) => set({ zone_id: t.zoneId, ward_id: t.wardId })} testIdPrefix={`${testId}-filter`} />
            </div>
          )}
          {isAdmin && (
            <div className="space-y-1">
              <Label className="text-xs">Supervisor</Label>
              <Select value={filters.supervisor_id || "all"} onValueChange={(v) => set({ supervisor_id: v === "all" ? "" : v, collector_id: "" })}>
                <SelectTrigger className="w-52 h-10" data-testid={`${testId}-filter-supervisor`}><SelectValue placeholder="All supervisors" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All supervisors</SelectItem>
                  {supervisors.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`${testId}-filter-supervisor-${u.id}`}>{staffLabelText(u.name, u.employee_id)}{u.is_active === false ? " (inactive)" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {!isCollector && (
            <div className="space-y-1">
              <Label className="text-xs">Collector</Label>
              <Select value={filters.collector_id || "all"} onValueChange={(v) => set({ collector_id: v === "all" ? "" : v })}>
                <SelectTrigger className="w-52 h-10" data-testid={`${testId}-filter-collector`}><SelectValue placeholder="All collectors" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{filters.supervisor_id ? "All collectors under this supervisor" : "All collectors"}</SelectItem>
                  {collectors.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`${testId}-filter-collector-${u.id}`}>{staffLabelText(u.name, u.employee_id)}{u.is_active === false ? " (inactive)" : ""}</SelectItem>)}
                  {collectors.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No collectors match the selected area / supervisor</div>}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <Select value={filters.status} onValueChange={(v) => set({ status: v })}>
              <SelectTrigger className="w-44 h-10" data-testid={`${testId}-filter-status`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="open">Open (scheduled + overdue)</SelectItem>
                <SelectItem value="scheduled">Scheduled (upcoming)</SelectItem>
                <SelectItem value="due_today">Due today</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="done">Done</SelectItem>
                <SelectItem value="superseded">Closed by newer visit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="w-48 h-10 pl-8" placeholder="PIN / owner / notes" value={filters.search} onChange={(e) => set({ search: e.target.value })} data-testid={`${testId}-filter-search`} />
            </div>
          </div>
          <label className="flex items-center gap-2 h-10 px-3 rounded-md border cursor-pointer" data-testid={`${testId}-filter-repeated`}>
            <Switch checked={filters.repeated_only} onCheckedChange={(v) => set({ repeated_only: !!v })} data-testid={`${testId}-filter-repeated-switch`} />
            <span className="text-xs inline-flex items-center gap-1"><Repeat className="h-3.5 w-3.5 text-orange-700" /> Repeated only</span>
          </label>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-10" onClick={() => set({ zone_id: "", ward_id: "", supervisor_id: "", collector_id: "", status: isCollector ? "open" : "all", repeated_only: false, search: "" })} data-testid={`${testId}-filter-reset`}>
              <Filter className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
            <Button variant="outline" className="h-10" onClick={doExport} disabled={exporting || loading} data-testid={`${testId}-export`}>
              <Download className="h-4 w-4 mr-1.5" /> {exporting ? "Preparing…" : "Export Excel"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          {loading && !data ? <div className="p-6"><Spinner /></div> : items.length === 0 ? (
            <div className="p-6"><EmptyState title="No follow-up visits match these filters" subtitle="Follow-ups appear here when a collector or supervisor schedules a return visit while logging a field visit, or when a 'Payment promised' closure auto-schedules one." /></div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid={`${testId}-table`}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">PIN / Owner</TableHead>
                    <TableHead className="text-xs hidden lg:table-cell min-w-[120px]">Ward · Zone · Mohalla</TableHead>
                    <TableHead className="text-xs min-w-[150px]">Entered by</TableHead>
                    <TableHead className="text-xs min-w-[130px]">Last visit</TableHead>
                    <TableHead className="text-xs">Scheduled follow-up</TableHead>
                    <TableHead className="text-xs hidden xl:table-cell">Notes</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs hidden md:table-cell">Completion</TableHead>
                    <TableHead className="text-xs text-right">Follow-ups</TableHead>
                    <TableHead className="text-xs text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r) => (
                    <TableRow key={r.id} className={`${r.fu_status === "overdue" ? "bg-red-50/30" : r.repeated ? "bg-orange-50/20" : ""}`} data-testid={`${testId}-row-${r.id}`} data-status={r.fu_status} data-repeated={r.repeated ? "true" : "false"}>
                      <TableCell className="text-xs align-top">
                        {propertyPath ? <Link to={propertyPath(r)} className="font-mono font-semibold text-primary hover:underline" data-testid={`${testId}-pin-${r.id}`}>{r.pin}</Link> : <span className="font-mono font-semibold">{r.pin}</span>}
                        <span className="block text-muted-foreground truncate max-w-[180px]">{r.owner_name || "—"}</span>
                        {r.repeated && <RepeatedBadge count={r.follow_up_count} className="mt-1" testId={`${testId}-repeated-${r.id}`} />}
                        {r.balance_due > 0 && <span className="block text-[11px] text-muted-foreground mt-0.5">{fmtINR(r.balance_due)} due</span>}
                      </TableCell>
                      <TableCell className="text-xs align-top hidden lg:table-cell text-muted-foreground">
                        <span className="block">{r.ward_label || "—"}</span>
                        <span className="block">{r.zone_label || "—"}</span>
                        <span className="block">{r.mohalla_label || "—"}</span>
                      </TableCell>
                      <TableCell className="text-xs align-top">
                        <span className="block font-medium" data-testid={`${testId}-staff-${r.id}`}>{staffLabel(r)}</span>
                        <span className="block text-[11px] text-muted-foreground">{r.staff_role_label}{r.auto_scheduled ? " · auto-scheduled" : ""}</span>
                      </TableCell>
                      <TableCell className="text-xs align-top whitespace-nowrap">
                        {r.last_visit_date ? fmtDate(r.last_visit_date) : "—"}
                        <span className="block text-[11px] text-muted-foreground truncate max-w-[160px]">{r.visit_outcome_label || ""}</span>
                      </TableCell>
                      <TableCell className="text-xs align-top whitespace-nowrap">
                        <span className={`font-medium ${r.fu_status === "overdue" ? "text-red-700" : ""}`} data-testid={`${testId}-date-${r.id}`}>{r.follow_up_date ? fmtDate(r.follow_up_date) : "—"}</span>
                        {r.reschedule_count > 0 && (
                          <span className="block text-[11px] text-muted-foreground" title={`Originally ${fmtDate(r.original_follow_up_date)}`} data-testid={`${testId}-rescheduled-${r.id}`}>
                            <History className="h-3 w-3 inline mr-0.5 -mt-0.5" /> Rescheduled ×{r.reschedule_count} · was {fmtDate(r.original_follow_up_date)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs align-top hidden xl:table-cell text-muted-foreground max-w-[220px]"><span className="line-clamp-2" title={r.notes || ""}>{r.notes || "—"}</span></TableCell>
                      <TableCell className="text-xs align-top">
                        <FollowUpStatusBadge status={r.fu_status} label={r.fu_status_label} testId={`${testId}-status-${r.id}`} />
                        {r.was_overdue && r.fu_status !== "overdue" && <span className="block text-[10px] text-red-700 mt-0.5">was overdue</span>}
                      </TableCell>
                      <TableCell className="text-xs align-top hidden md:table-cell">
                        {r.fu_status === "done" ? (
                          <div data-testid={`${testId}-completion-${r.id}`}>
                            <span className="block">{r.done_date ? fmtDate(r.done_date) : "—"}{r.done_by_name ? ` · ${r.done_by_name}` : ""}</span>
                            <span className="block font-medium">{r.done_reason_label || "—"}{r.promised_date ? ` → ${fmtDate(r.promised_date)}` : ""}</span>
                            {r.done_remarks && <span className="block text-muted-foreground line-clamp-2" title={r.done_remarks}>{r.done_remarks}</span>}
                          </div>
                        ) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-xs align-top text-right tabular-nums" data-testid={`${testId}-count-${r.id}`}>{r.follow_up_count}</TableCell>
                      <TableCell className="text-xs align-top text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button variant="ghost" size="sm" className="h-8" onClick={() => setDetail(r)} data-testid={`${testId}-detail-${r.id}`}>Details</Button>
                          {canAct && <FollowUpRowActions code={code} item={r} onChanged={load} compact testIdPrefix={`${testId}-act`} />}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {data && data.total > PAGE && (
            <div className="flex items-center justify-between px-4 py-3 border-t text-xs text-muted-foreground" data-testid={`${testId}-pagination`}>
              <span>Showing {(page - 1) * PAGE + 1}–{Math.min(page * PAGE, data.total)} of {data.total}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`${testId}-prev`}>Previous</Button>
                <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid={`${testId}-next`}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <FollowUpDetailSheet code={code} item={detail} onClose={() => setDetail(null)} propertyPath={propertyPath} canAct={canAct} onChanged={() => { load(); setDetail(null); }} testId={`${testId}-sheet`} />
    </div>
  );
}

function FollowUpDetailSheet({ code, item, onClose, propertyPath, canAct, onChanged, testId }) {
  const [history, setHistory] = useState(null);
  useEffect(() => {
    setHistory(null);
    if (!item) return;
    api.get(`/t/${code}/follow-up-visits/${item.id}`).then((r) => setHistory(r.data?.history || [])).catch(() => setHistory([]));
  }, [code, item]);
  if (!item) return null;
  const meta = FU_STATUS_META[item.fu_status] || FU_STATUS_META.scheduled;
  return (
    <Sheet open={!!item} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid={testId}>
        <SheetHeader>
          <SheetTitle className="font-heading flex items-center gap-2 flex-wrap">
            Follow-up · <span className="font-mono">{item.pin}</span>
            <FollowUpStatusBadge status={item.fu_status} label={item.fu_status_label} />
            {item.repeated && <RepeatedBadge count={item.follow_up_count} />}
          </SheetTitle>
          <SheetDescription>
            {propertyPath ? <Link to={propertyPath(item)} className="font-semibold text-primary hover:underline" onClick={onClose}>{item.owner_name || "—"}</Link> : (item.owner_name || "—")}
            {" · "}{[item.zone_label, item.ward_label, item.mohalla_label].filter(Boolean).join(" › ") || "—"}{item.balance_due > 0 ? ` · ${fmtINR(item.balance_due)} due` : ""}
          </SheetDescription>
        </SheetHeader>
        <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
          {[["Entered by", `${item.staff_name || "—"} (${item.staff_role_label})`], ["Last visit", item.last_visit_date ? `${fmtDate(item.last_visit_date)}${item.last_visit_time ? ` ${item.last_visit_time.slice(0, 5)}` : ""}` : "—"],
            ["Scheduled follow-up", item.follow_up_date ? fmtDate(item.follow_up_date) : "—"], ["Originally scheduled", item.original_follow_up_date ? fmtDate(item.original_follow_up_date) : "—"],
            ["Visit outcome", item.visit_outcome_label || "—"], ["Follow-ups since last payment", `${item.follow_up_count}${item.last_payment_at ? ` (last payment ${fmtDate(item.last_payment_at.slice(0, 10))})` : " (no payment on record)"}`]].map(([l, v]) => (
            <div key={l} className="rounded-lg border px-3 py-2">
              <p className="text-[11px] text-muted-foreground">{l}</p>
              <p className="font-medium text-xs">{v}</p>
            </div>
          ))}
        </div>
        {item.notes && <p className="text-xs mt-3 rounded-md border bg-muted/40 px-3 py-2" data-testid={`${testId}-notes`}><b>Notes:</b> {item.notes}</p>}
        <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${meta.cls}`} data-testid={`${testId}-status-box`}>
          {item.fu_status === "done" ? (
            <>
              <p className="font-semibold">Done on {item.done_date ? fmtDate(item.done_date) : "—"}{item.done_by_name ? ` by ${item.done_by_name}` : ""}{item.was_overdue ? " (was overdue)" : ""}</p>
              <p>Reason: <b>{item.done_reason_label || "—"}</b>{item.promised_date ? ` — payment promised for ${fmtDate(item.promised_date)}` : ""}</p>
              {item.done_remarks && <p>Remarks: {item.done_remarks}</p>}
            </>
          ) : item.fu_status === "overdue" ? (
            <p className="font-semibold">Overdue — was due {fmtDate(item.follow_up_date)} and has not been marked Done.</p>
          ) : item.fu_status === "superseded" ? (
            <p>Closed automatically because a newer visit was logged on this property.</p>
          ) : (
            <p>Open — {item.fu_status_label.toLowerCase()}.</p>
          )}
        </div>
        {(item.reschedules || []).length > 0 && (
          <div className="mt-4" data-testid={`${testId}-reschedules`}>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><History className="h-4 w-4 text-muted-foreground" /> Reschedule log ({item.reschedules.length})</h3>
            <ol className="space-y-1.5">
              {[...item.reschedules].reverse().map((r, i) => (
                <li key={i} className="text-xs rounded-md border px-3 py-2">
                  <span className="font-medium">{r.from ? fmtDate(r.from) : "no date"} → {fmtDate(r.to)}</span>
                  <span className="text-muted-foreground"> · {fmtDateTime(r.at)}{r.by_name ? ` · ${r.by_name}` : ""}{r.was_overdue ? " · was overdue" : ""}</span>
                  {r.note && <span className="block text-muted-foreground">{r.note}</span>}
                </li>
              ))}
            </ol>
          </div>
        )}
        <div className="mt-4" data-testid={`${testId}-history`}>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><Users className="h-4 w-4 text-muted-foreground" /> Other follow-ups on this property</h3>
          {history == null ? <Spinner /> : history.length === 0 ? <p className="text-xs text-muted-foreground">None.</p> : (
            <ul className="space-y-1.5">
              {history.map((h) => (
                <li key={h.id} className="text-xs rounded-md border px-3 py-2 flex items-center justify-between gap-2">
                  <span>{h.follow_up_date ? fmtDate(h.follow_up_date) : "—"} · {h.staff_name || "—"}{h.done_reason_label ? ` · ${h.done_reason_label}` : ""}</span>
                  <FollowUpStatusBadge status={h.fu_status} label={h.fu_status_label} />
                </li>
              ))}
            </ul>
          )}
        </div>
        {canAct && (item.status || "open") === "open" && (
          <div className="mt-5 flex justify-end"><FollowUpRowActions code={code} item={item} onChanged={onChanged} testIdPrefix={`${testId}-act`} /></div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default FollowUpVisitsReport;
